import { CatalogProductProps } from './catalog-product.type'
import { CurrencyEnum } from './currency.type'
import { ProfileProps } from './profiles.type'
import { SaleOrderItemProps, SaleOrderProps } from './sale-order.type'
import { SupplierProductProps } from './supplier-product.type'
import { SupplyTransactionProps } from './supplier-transaction.type'
import { SupplierProps, SupplierWarehouseProps } from './supplier.type'

export enum ProductQualityCategoryEnum {
  'safety_stock' = 'safety_stock',
  'sale' = 'sale',
  'sample' = 'sample',
  'defect' = 'defect',
}

export interface SupplyChainLinkProps {
  uuid?: string
  saleOrder?: SaleOrderProps
  saleOrderItem?: SaleOrderItemProps
  saleOrderItemUuid?: string
  supplierOrderItemUuid?: string
  quantity?: number
  createdAt?: string
  updatedAt?: string
}

export enum PurchaseOrderDeliveryOptionEnum {
  'keep_at_supplier' = 'keep_at_supplier',
  'deliver_to_our_warehouse' = 'deliver_to_our_warehouse',
}

export interface SupplyQuotationItem {
  uuid?: string
  quantity?: number
  total?: number
  cost?: string
  reference?: string
  catalogProductUuid?: string
  catalogProduct?: CatalogProductProps
  supplierUuid?: string
  supplier: SupplierProps
  buyerUuid?: string
  buyer: ProfileProps
  supplyQuotationUuid?: string
  saleOrderItemUuid?: string
  saleOrderItem?: {
    uuid: string
    order: string
  }
  createdAt?: string
  updatedAt?: string
  deliveryOption?: keyof typeof PurchaseOrderDeliveryOptionEnum
  supplierWarehouseUuid?: string
  supplierWarehouse?: SupplierWarehouseProps
}

export interface SupplierOrderItemSaleOrderItem {
  uuid?: string
  catalogProduct?: CatalogProductProps
  catalogProductUuid?: string
  quantity?: number
  cost?: string
  price?: string
  deliveryOption?: keyof typeof PurchaseOrderDeliveryOptionEnum
}

export interface SupplyOrderItemProps {
  uuid?: string
  catalogProductUuid?: string
  catalogProduct?: CatalogProductProps
  cost?: string
  costWithoutIva?: string
  deliveryOption?: keyof typeof PurchaseOrderDeliveryOptionEnum
  category?: keyof typeof ProductQualityCategoryEnum
  dollarExchangeRate?: string
  quantity?: number
  total?: string
  totalWithoutIva?: string
  reference?: string
  supplierUuid?: string
  supplier?: SupplierProps
  supplyQuotationUuid?: string
  saleOrderItemUuid?: string
  supplyChainLink?: SupplyChainLinkProps[]
  createdAt?: string
  updatedAt?: string
  supplyOrder?: string
  supplierWarehouseUuid?: string
  supplierWarehouse?: SupplierWarehouseProps
}

export enum SupplierInvoiceStatusEnum {
  'pending' = 'pending',
  'completed' = 'completed',
}

export interface SupplierInvoiceProps {
  uuid?: string
  pdfFile?: string
  xmlFile?: string
  status: keyof typeof SupplierInvoiceStatusEnum
  supplierUuid: string
  supplierOrderUuid: string
  createdAt?: string
  updatedAt?: string
}

export interface SupplyStatementProps {
  quantity?: number
  paymentUuid?: string
  payment?: SupplyPaymentProps
  transactionUuid?: string
  transaction?: SupplyTransactionProps
  createdAt?: string
  updatedAt?: string
}

export enum SupplierPaymentPriorityEnum {
  'urgent' = 'urgent',
  'high' = 'high',
  'normal' = 'normal',
  'low' = 'low',
}

export enum SupplyPaymentStatusEnum {
  'pending' = 'pending',
  'paid' = 'paid',
  'payment_required' = 'payment_required',
  'cancelled' = 'cancelled',
}

export enum SupplyPaymentTypeEnum {
  'cost_of_goods' = 'cost_of_goods',
  'weight_adjustment' = 'weight_adjustment',
  'transportation_charge' = 'transportation_charge',
  'merchandise_insurance' = 'merchandise_insurance',
}

export interface SupplyPaymentProps {
  uuid?: string
  currency?: keyof typeof CurrencyEnum
  discount?: string
  dollarExchangeRate?: string
  expectedPaymentDate?: string
  paymentAt?: string
  value?: number
  iva?: number
  subtotal?: number
  status?: keyof typeof SupplyPaymentStatusEnum
  supplierUuid?: string
  supplyOrderUuid?: string
  paymentPriority?: keyof typeof SupplierPaymentPriorityEnum
  statements?: SupplyStatementProps[]
  observation?: string
  type?: keyof typeof SupplyPaymentTypeEnum
  createdAt?: string
  updatedAt?: string
}

export enum SupplyQuotationStatus {
  'pending' = 'pending',
  'accepted' = 'accepted',
  'cancelled' = 'cancelled',
}

export interface SupplyQuotationProps {
  uuid?: string
  expectedPaymentDate?: string
  status: keyof typeof SupplyQuotationStatus
  iva: number
  subtotal: number
  value: number
  discount?: number
  currency: keyof typeof CurrencyEnum
  dollarExchangeRate?: string
  purchaseOrderFile?: string
  purchaseOrderCode?: string
  supplierUuid?: string
  supplier: SupplierProps
  items: SupplyQuotationItem[]
  sentPo?: boolean
  destinationAddress?: string
  createdAt?: string
  updatedAt?: string
}

export enum SupplyOrderStatusEnum {
  'pending' = 'pending',
  'completed' = 'completed',
  'cancelled' = 'cancelled',
}

export enum SupplyOrderShipmentStatusEnum {
  in_collection = "in_collection",
  on_the_way = "on_the_way",
  not_started = "not_started",
  delivered = "delivered",
  about_to_deliver = "about_to_deliver",
  collection_tomorrow = "collection_tomorrow",
  crossing = "crossing",
  unloading = "unloading",
}

export enum SupplyOrderStatusCancelledReasonEnum {
  partial_deliveries = 'partial_deliveries',
  lack_of_material = 'lack_of_material',
  poor_material_quality = 'poor_material_quality',
  not_the_expected_material = 'not_the_expected_material',
  replaced_by_new_one = 'replaced_by_new_one',
  sale_order_cancellation = 'sale_order_cancellation',
}

export interface SupplierOrderCreatorUserProps {
  id: string
  team?: string
  username?: string
  name: string
  email?: string
  phone?: string
  isActive?: boolean
  allowEmail?: boolean
  allowWhatsapp?: boolean
  dateJoined?: string
  deactivatedAt?: null
  isStaff?: boolean
  isSuperuser?: boolean
  lastLogin?: string
  selfCreated?: boolean
  profile?: ProfileProps
}

export interface SupplyOrderProps {
  uuid?: string
  creatorUser?: SupplierOrderCreatorUserProps
  creatorUserId?: string
  includeIva?: boolean
  items?: SupplyOrderItemProps[]
  iva?: string
  observation?: string
  reference?: string
  saleOrders?: SaleOrderProps[]
  status: keyof typeof SupplyOrderStatusEnum
  shipmentStatus: keyof typeof SupplyOrderShipmentStatusEnum
  shipmentCustomCertificateFile?: string
  shipmentStatusHistory?: Record<string, string>
  cancellationReason?: keyof typeof SupplyOrderStatusCancelledReasonEnum
  cancellationDescription?: string
  supplier?: SupplierProps
  supplierUuid?: string
  supplyQuotationUuid?: string
  supplyQuotation?: SupplyQuotationProps
  total?: string
  totalWithoutIva?: string
  invoice?: SupplierInvoiceProps
  invoiceUuid?: string
  payments?: SupplyPaymentProps[]
  action?: string
  createdAt?: string
  updatedAt?: string
}

export interface SupplierOrderWithPagesProps {
  count: number
  next?: string
  previous?: string
  results?: SupplyOrderProps[]
}

export interface GeneratePurchaseOrderResponse {
  currency: keyof typeof CurrencyEnum
  dollarExchangeRate: string
  saleOrders?: SaleOrderProps[]
  supplierProducts?: SupplierProductProps[]
  total: number
}

export interface AdditionalProductResponse {
  cost: string
  quantity: number
  total: string
  warehouseUuid?: string
  catalogProduct: CatalogProductProps
  deliveryOption: keyof typeof PurchaseOrderDeliveryOptionEnum
  category?: keyof typeof ProductQualityCategoryEnum
}

export interface GeneratePurchaseOrderPreviewResponse {
  currency: keyof typeof CurrencyEnum
  dollarExchangeRate: number
  totalWithoutIva: number
  total: number
  iva: number
  saleOrders?: SaleOrderProps[]
  additionalProducts?: AdditionalProductResponse[]
}

export interface SupplyOrderPreviewResponse {
  currency: keyof typeof CurrencyEnum
  dollarExchangeRate: number
  totalWithoutIva: number
  total: number
  iva: number
  items: {
    quantity?: number
    cost?: string
    costWithoutIva?: string
    total?: string
    totalWithoutIva?: string
  }[]
}
export interface GeneratePurchaseOrderPayloadAdditionalProductProps {
  uuid?: string
  cost?: string
  quantity?: number
  supplierWarehouseUuid?: string
  supplierWarehouse?: SupplierWarehouseProps
  deliveryOption?: keyof typeof PurchaseOrderDeliveryOptionEnum
  catalogProductUuid?: string
  catalogProduct?: CatalogProductProps
  category?: keyof typeof ProductQualityCategoryEnum
}
export interface GeneratePurchaseOrderPayloadSupplyItemProps {
  uuid?: string
  generatePo?: boolean
  quantity?: number
  category?: keyof typeof ProductQualityCategoryEnum
}
export interface GeneratePurchaseOrderPayloadSaleOrderItemProps {
  uuid?: string
  supplyItems: GeneratePurchaseOrderPayloadSupplyItemProps[]
}

export interface GeneratePurchaseOrderPayloadSaleOrdersProps {
  reference?: string
  buyerUuid?: string
  items: GeneratePurchaseOrderPayloadSaleOrderItemProps[]
}

export interface GeneratePurchaseOrderPayload {
  currency?: 'MXN' | 'USD'
  purchaseOrderCode?: string
  paymentPriority?: keyof typeof SupplierPaymentPriorityEnum
  expectedPaymentDate?: string
  observation?: string
  status?: keyof typeof SupplyQuotationStatus
  supplierUuid?: string
  supplierWarehouseUuid?: string
  saleOrders?: GeneratePurchaseOrderPayloadSaleOrdersProps[]
  sendPo?: boolean
  additionalProducts?: GeneratePurchaseOrderPayloadAdditionalProductProps[]
}

export interface GeneratePurchaseOrderPreviewPayloadSaleOrderItem {
  uuid?: string
  quantity: number
}

export interface GeneratePurchaseOrderPreviewPayloadSaleOrders {
  reference?: string
  items: GeneratePurchaseOrderPreviewPayloadSaleOrderItem[]
}

export interface GeneratePurchaseOrderPreviewPayload {
  supplierUuid?: string
  currency?: 'MXN' | 'USD'
  saleOrders?: GeneratePurchaseOrderPreviewPayloadSaleOrders[]
  additionalProducts?: GeneratePurchaseOrderPayloadAdditionalProductProps[]
}

export interface SupplyOrderPreview {
  includeIva?: boolean
  items?: {
    quantity?: number
    cost?: number
  }[]
}

export interface SupplyItemsPreviewPayload {
  // array of references
  saleOrders: string[]
}

export interface SupplyItemsPreviewResponseSupplyOrderItem {
  uuid?: string
  reference?: string
  supplyChainLink?: SupplyChainLinkProps[]
  supplyOrder?: SupplyOrderProps
  quantity?: number
  availableQuantity?: number
  cost?: string
  costWithoutIva?: string
  totalWithoutIva?: string
  dollarExchangeRate?: string
  deliveryOption?: keyof typeof PurchaseOrderDeliveryOptionEnum
  category?: keyof typeof ProductQualityCategoryEnum
}

export interface SupplyItemsPreviewResponseSaleOrderItem {
  uuid?: string
  title?: string
  cost?: string
  quantity?: number
  missingQuantity?: number
  supplyOrderItems?: SupplyItemsPreviewResponseSupplyOrderItem[]
  supplyChainLinks?: SupplyChainLinkProps[]
}
export interface SupplyItemsPreviewResponseSaleOrder {
  uuid?: string
  reference?: string
  payments: {
    dollarExchangeRate?: string
    currency: 'MXN' | 'USD'
  }[]
  destinationAddress?: string
  buyer?: {
    companyName?: string
  }
  items: SupplyItemsPreviewResponseSaleOrderItem[]
}

export interface SupplyItemsPreviewResponse {
  saleOrders: SupplyItemsPreviewResponseSaleOrder[]
}

export interface SupplyOrderUpdateManagementPayload {
  supplyOrder: Partial<SupplyOrderProps>
  supplyChainLinkUuid?: string[]
}

export interface SupplyPaymentRequiredPayload {
  supplyOrdersReference: string[]
  observation?: string
  expectedPaymentDate?: string
  paymentPriority?: string
}